// src/components/stream/KeepaliveHost.tsx
import { useCallback, useEffect, useReducer, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { getMe } from "../../api/user";
import { onRabbitChannelEvent, useRabbitMQ, type RBEvent } from "../../hooks/useRabbitMQ";
import { retainServerClock, serverClock } from "../../lib/castSync/serverClock";
import { slotEntries } from "../../lib/streamSlot";
import { useAuthStore } from "../../store/authStore";
import { firstPrompt, keepaliveContextOf, useKeepaliveStore } from "../../store/keepaliveStore";
import { useOwnCastStore, type OwnCastState } from "../../store/ownCastStore";
import { useStreamStatusStore, type StreamStatusState } from "../../store/streamStatusStore";
import KeepaliveDialog from "./KeepaliveDialog";

/** How often the host re-picks the prompt to show while it holds several. */
const HOST_TICK_MS = 1_000;

/** Busy VMPL_LINK slot ids in the slot cache, sorted, joined: a primitive selector. */
export function busyLinkSlotKey(s: StreamStatusState): string {
  const ids: number[] = [];
  for (const info of Object.values(s.infoByGc)) {
    for (const { slot } of slotEntries(info)) if (slot.streamContentType === "VMPL_LINK" && slot.available === false) ids.push(slot.id);
  }
  return ids.sort((a, b) => a - b).join(",");
}

/** My own link cast's slot id, or "" (its GC may not be in the slot cache). */
const ownLinkSlotKey = (s: OwnCastState): string => String(s.casts.VMPL_LINK_1?.slot.id ?? "");

const idsOf = (...keys: string[]): number[] =>
  [...new Set(keys.flatMap((k) => (k === "" ? [] : k.split(",").map(Number))))].sort((a, b) => a - b);

/** Every slot to ask GET queue for after events may have been missed (W6). */
const slotsToRefresh = (): number[] =>
  idsOf(
    busyLinkSlotKey(useStreamStatusStore.getState()),
    ownLinkSlotKey(useOwnCastStore.getState()),
    Object.keys(useKeepaliveStore.getState().bySlot).join(","),
  );

/**
 * The keep-alive prompt's one host (spec 2026-10-19 §4.4), mounted once beside StreamStatusBridge.
 * Events from the shared RabbitMQ bus; GET queue on join, reconnect and back to the tab (or from a
 * PiP window: the main window regains focus); the dialog for the prompt that runs out first.
 */
export default function KeepaliveHost() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const channel = useAuthStore((s) => s.channel);
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe, enabled: isAuthenticated });
  const meId = isAuthenticated ? (me?.id ?? null) : null;
  const meRef = useRef(meId);
  meRef.current = meId;

  const onEvent = useCallback((e: RBEvent) => useKeepaliveStore.getState().fromEvent(e, keepaliveContextOf(meRef.current)), []);
  useRabbitMQ(isAuthenticated ? channel : null, onEvent, "KeepaliveHost");

  useEffect(() => {
    if (!isAuthenticated) useKeepaliveStore.getState().reset();
  }, [isAuthenticated]);

  // Late join: each busy link slot, or my own link cast's slot, seen for the first time (once `me` is known).
  const linkKey = useStreamStatusStore(busyLinkSlotKey);
  const ownKey = useOwnCastStore(ownLinkSlotKey);
  const seen = useRef(new Set<number>());
  useEffect(() => {
    if (meId == null) return;
    const ids = idsOf(linkKey, ownKey);
    for (const id of ids) if (!seen.current.has(id)) useKeepaliveStore.getState().refresh(id, keepaliveContextOf(meId));
    seen.current = new Set(ids);
  }, [linkKey, ownKey, meId]);

  // Prompts are judged on the server clock: keep it synced even when no cast player is mounted
  // (a viewer who never opened one), while a link slot is busy, my own link cast runs, or a prompt is held.
  const holdsAny = useKeepaliveStore((s) => Object.keys(s.bySlot).length > 0);
  const needsClock = isAuthenticated && (linkKey !== "" || ownKey !== "" || holdsAny);
  useEffect(() => (needsClock ? retainServerClock() : undefined), [needsClock]);

  // Reconnect, back from the background or from a PiP window: events may have been missed.
  useEffect(() => {
    const again = () => {
      const meNow = meRef.current;
      if (meNow == null) return;
      for (const id of slotsToRefresh()) useKeepaliveStore.getState().refresh(id, keepaliveContextOf(meNow));
    };
    const off = onRabbitChannelEvent((e) => {
      if (e === "reconnected") again();
    });
    const onVisible = () => {
      if (document.visibilityState === "visible") again();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", again);
    return () => {
      off();
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", again);
    };
  }, []);

  // Which prompt shows changes with time too (a CASTER prompt held at 0 s yields to a live one).
  const bySlot = useKeepaliveStore((s) => s.bySlot);
  const [, tick] = useReducer((n: number) => n + 1, 0);
  const holdsSeveral = Object.keys(bySlot).length > 1;
  useEffect(() => {
    if (!holdsSeveral) return;
    const id = setInterval(tick, HOST_TICK_MS);
    return () => clearInterval(id);
  }, [holdsSeveral]);
  const prompt = firstPrompt(bySlot, serverClock.now());
  if (!prompt || meId == null) return null;
  return <KeepaliveDialog key={`${prompt.slotId}:${prompt.promptId}`} prompt={prompt} me={meId} />;
}
